import { Controller } from "@hotwired/stimulus"

// Synchronizuje pola formularza z JSON-em w textarea fields.
// Obsługuje dwa tryby:
// 1. Obiekt (fields) — input z data-key="brand_color" ↔ fields JSON { "brand_color": "xxx" }
// 2. Tablica (domains) — input z data-array-for="product_app_domains" ↔ textarea z JSON ["xxx"]
//    wartość inputa = pierwszy element tablicy, wpisanie zmienia tablicę na [value]
//
// Użycie:
//   <form data-controller="fields-form">
//     <%# obiekt — fields %>
//     <textarea data-fields-form-target="json" id="fields_json">...</textarea>
//     <input data-fields-form-target="field" data-key="brand_color"
//            data-action="input->fields-form#fieldChanged">
//
//     <%# tablica — domains %>
//     <textarea data-fields-form-target="arrayJson" id="product_app_domains">...</textarea>
//     <input data-fields-form-target="arrayField" data-array-for="product_app_domains"
//            data-action="input->fields-form#arrayFieldChanged">
//   </form>
export default class extends Controller {
  static targets = ["json", "field", "arrayJson", "arrayField"]

  connect() {
    this.updating = false
    this.syncFromJson()
    this.syncArrayFields()
    this.element.addEventListener("submit", this.mergeBeforeSubmit.bind(this))
  }

  disconnect() {
    this.element.removeEventListener("submit", this.mergeBeforeSubmit.bind(this))
  }

  // --- Obiekt (fields) ---

  syncFromJson() {
    const data = this.parseJson()
    if (!data) return

    this.fieldTargets.forEach((input) => {
      const key = input.dataset.key
      if (key && data[key] !== undefined) {
        if (input.type === "checkbox") {
          input.checked = data[key] === true
        } else {
          input.value = data[key]
        }
      }
    })
  }

  fieldChanged() {
    if (this.updating) return
    this.updating = true
    this.mergeFieldsToJson()
    this.updating = false
  }

  jsonChanged() {
    if (this.updating) return
    this.updating = true
    this.syncFromJson()
    this.updating = false
  }

  mergeBeforeSubmit() {
    this.mergeFieldsToJson()
    this.mergeArrayFieldsToJson()
  }

  mergeFieldsToJson() {
    const data = this.parseJson() || {}

    this.fieldTargets.forEach((input) => {
      const key = input.dataset.key
      if (!key) return

      if (input.type === "checkbox") {
        data[key] = input.checked
      } else {
        const value = input.value.trim()
        if (value === "") {
          delete data[key]
        } else {
          data[key] = value
        }
      }
    })

    const json = JSON.stringify(data, null, 2)
    this.jsonTarget.value = json

    const cmWrapper = this.jsonTarget.closest("[data-controller='codemirror']")
    if (cmWrapper && cmWrapper.cmView) {
      const view = cmWrapper.cmView
      view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: json }
      })
    }
  }

  parseJson() {
    try {
      const cmWrapper = this.jsonTarget.closest("[data-controller='codemirror']")
      const raw = (cmWrapper && cmWrapper.cmView)
        ? cmWrapper.cmView.state.doc.toString()
        : this.jsonTarget.value
      return JSON.parse(raw || "{}")
    } catch {
      return null
    }
  }

  // --- Tablica (domains itp.) ---

  // wczytaj pierwszy element tablicy do inputa
  syncArrayFields() {
    this.arrayFieldTargets.forEach((input) => {
      const textarea = this.findArrayTextarea(input)
      if (!textarea) return

      const arr = this.parseArrayJson(textarea)
      if (arr && arr.length > 0) {
        input.value = arr[0]
      }
    })
  }

  // po zmianie inputa — ustaw [value] jako pierwszy element tablicy
  arrayFieldChanged(event) {
    if (this.updating) return
    this.updating = true

    const input = event.target
    const textarea = this.findArrayTextarea(input)
    if (!textarea) { this.updating = false; return }

    const arr = this.parseArrayJson(textarea) || []
    const value = input.value.trim()

    if (value === "") {
      arr.shift()
    } else if (arr.length === 0) {
      arr.push(value)
    } else {
      arr[0] = value
    }

    const json = JSON.stringify(arr, null, 2)
    textarea.value = json

    const cmWrapper = textarea.closest("[data-controller='codemirror']")
    if (cmWrapper && cmWrapper.cmView) {
      const view = cmWrapper.cmView
      view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: json }
      })
    }

    this.updating = false
  }

  // po zmianie textarea tablicy (np. z CodeMirror) — zaktualizuj input
  arrayJsonChanged(event) {
    if (this.updating) return
    this.updating = true

    const textarea = event.target
    const id = textarea.id
    const input = this.arrayFieldTargets.find((f) => f.dataset.arrayFor === id)
    if (input) {
      const arr = this.parseArrayJson(textarea)
      input.value = (arr && arr.length > 0) ? arr[0] : ""
    }

    this.updating = false
  }

  mergeArrayFieldsToJson() {
    this.arrayFieldTargets.forEach((input) => {
      const textarea = this.findArrayTextarea(input)
      if (!textarea) return

      const arr = this.parseArrayJson(textarea) || []
      const value = input.value.trim()

      if (value === "" && arr.length > 0) {
        arr.shift()
      } else if (value !== "" && arr.length === 0) {
        arr.push(value)
      } else if (value !== "") {
        arr[0] = value
      }

      textarea.value = JSON.stringify(arr, null, 2)
    })
  }

  findArrayTextarea(input) {
    const targetId = input.dataset.arrayFor
    return targetId ? document.getElementById(targetId) : null
  }

  parseArrayJson(textarea) {
    try {
      const cmWrapper = textarea.closest("[data-controller='codemirror']")
      const raw = (cmWrapper && cmWrapper.cmView)
        ? cmWrapper.cmView.state.doc.toString()
        : textarea.value
      const parsed = JSON.parse(raw || "[]")
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }
}
